import { Badge, Box, Flex, Group, Title, Text, Grid } from '@mantine/core';
import { formatDate } from 'utils/dates';
import { CenteredLoading } from 'components/CenteredLoading';
import { DrawerActionButton } from 'components/Table/Drawer/DrawerActionButton';
import { BsArrowLeft, BsArrowRight } from 'react-icons/bs';
import { useTranslation } from 'react-i18next';
import { getWeightIndicatorColor } from 'utils/measurements/weight';
import { calculateBmiIndicator, displayMeasurementWeightUnitsLabel } from 'utils/helpers';
import { useWeightMeasurementById } from 'hooks/api/useWeightData';
import { queryParams } from 'utils/query';
import { useParameters } from 'hooks/useParameters';
import { UnitType } from 'models/generic';

interface WeightDetailsProps {
  moreIds?: string[];
  unitSystem: UnitType;
}

export const WeightDetails = ({ moreIds, unitSystem }: WeightDetailsProps) => {
  const { t } = useTranslation();
  const { parameters: filters, setParameters } = useParameters();
  const { data: selectedWeightMeasurement } = useWeightMeasurementById({
    id: filters[queryParams.detailId],
  });
  const currentIndex = moreIds?.findIndex((value) => value === selectedWeightMeasurement?.id) || 0;

  const handleNextMeasurement = () => {
    if (moreIds) setParameters({ ...filters, [queryParams.detailId]: moreIds[currentIndex + 1] });
  };

  const handlePreviousMeasurement = () => {
    if (moreIds) setParameters({ ...filters, [queryParams.detailId]: moreIds[currentIndex - 1] });
  };

  if (!selectedWeightMeasurement) return <CenteredLoading />;

  return (
    <Box>
      <Flex justify={'space-between'}>
        <Title mb={'xl'} mt={'xs'} order={3}>
          {t('weight_bmi_details_label')}
        </Title>
        <Group mb={'xl'} mt={'xs'}>
          {moreIds && (
            <Group gap={'xs'}>
              <DrawerActionButton
                tooltipLabel={t('patients_previous_measurement')}
                disabled={(currentIndex || 0) <= 0}
                onClick={() => handlePreviousMeasurement()}
                Icon={BsArrowLeft}
                isOutlineVariant
              />
              <DrawerActionButton
                tooltipLabel={t('patients_next_measurement')}
                disabled={currentIndex === moreIds.length - 1}
                onClick={() => handleNextMeasurement()}
                Icon={BsArrowRight}
                isOutlineVariant
              />
            </Group>
          )}
        </Group>
      </Flex>

      <Badge
        color={getWeightIndicatorColor(calculateBmiIndicator(selectedWeightMeasurement?.data?.value))}
        size={'lg'}
        radius={'sm'}
        variant={'filled'}
        mb={'xs'}
      >
        {t(`diagnosis_${calculateBmiIndicator(selectedWeightMeasurement?.data?.value)}`)}
      </Badge>
      <Group>
        <Text size={'sm'} mb={'xl'} fs={'italic'}>
          {formatDate({ timestamp: selectedWeightMeasurement?.data?.recordedTimestamp || 0, formatWithTime: true })}
        </Text>
      </Group>
      <Grid>
        <Grid.Col span={3}>{t('weight_label')}</Grid.Col>
        <Grid.Col span={9}>
          {displayMeasurementWeightUnitsLabel({ weight: selectedWeightMeasurement?.data?.input?.weight, unitSystem })}
        </Grid.Col>
        <Grid.Col span={3}>{t('bmi_label')}</Grid.Col>
        <Grid.Col span={9}>{selectedWeightMeasurement?.data?.value?.toFixed(1)}</Grid.Col>
      </Grid>
    </Box>
  );
};
